import { useQuery } from 'react-query'
import { scopeKey } from '../utils/process-list-cache'
import { TABLE_PAGE_SIZE } from '@/components/ui/TablePagination'
import { occurrencesService } from '../../../services/occurrencesService'
import type {
  ListScopeFilter,
  ProcessListFilter,
  ProcessListItem,
  ProcessSortKey,
} from '../types/occurrences'

export const PROCESS_LIST_QUERY_KEY = ['occurrences', 'process-list'] as const


/**
 * Uma página da lista de processos do escopo do usuário (páginas numeradas,
 * ordenação no servidor). Filtros, página e ordenação entram na chave para
 * isolar caches; `keepPreviousData` mantém a página anterior na tela enquanto
 * a próxima carrega.
 */
export function useProcessList(opts: {
  filter?: ProcessListFilter
  search?: string
  /** Empresas e analistas; ausente = sem restrição. */
  scope?: ListScopeFilter
  page?: number
  pageSize?: number
  sort?: { key: ProcessSortKey; dir: 'asc' | 'desc' } | null
  /** `false` pausa a lista (escondida atrás de um detalhe aberto). */
  enabled?: boolean
} = {}) {
  const pageSize = opts.pageSize ?? TABLE_PAGE_SIZE
  const page = opts.page ?? 1
  return useQuery(
    [
      ...PROCESS_LIST_QUERY_KEY,
      opts.filter ?? 'all',
      opts.search ?? '',
      scopeKey(opts.scope),
      pageSize,
      page,
      opts.sort ? `${opts.sort.key}:${opts.sort.dir}` : 'default',
    ],
    () =>
      occurrencesService.listProcesses({
        filter: opts.filter,
        search: opts.search,
        scope: opts.scope,
        page,
        limit: pageSize,
        sort: opts.sort?.key,
        dir: opts.sort?.dir,
      }),
    {
      enabled: opts.enabled ?? true,
      staleTime: 30 * 1000,
      refetchOnMount: true,
      keepPreviousData: true,
    },
  )
}

/**
 * Contadores agregados para os chips e indicadores da lista, isolados do
 * filtro ativo. Sem isso, ao clicar em "Menção" os totais de Críticos/Todos
 * cairiam para zero (o `totals` da lista paginada reflete só o subset
 * filtrado). Respeita `search` e `scope` — esses devem afetar os
 * indicadores —, mas nunca o `filter` (mention/critical/all).
 *
 * Faz duas queries paralelas porque o backend não retorna `mention` no
 * `totals`: uma sem filter (pega all/critical/late/with_open_occurrence) e
 * outra com filter=mention só pra pegar `total_processes` desse subset.
 */
export function useProcessListCounters(opts: {
  search?: string
  scope?: ListScopeFilter
  enabled?: boolean
} = {}) {
  const baseKey = [
    ...PROCESS_LIST_QUERY_KEY,
    'counters',
    opts.search ?? '',
    scopeKey(opts.scope),
  ] as const

  // `limit: 1` economiza payload — só queremos o objeto `totals`.
  const allQuery = useQuery(
    [...baseKey, 'all'],
    () =>
      occurrencesService.listProcesses({
        search: opts.search,
        scope: opts.scope,
        page: 1,
        limit: 1,
      }),
    { staleTime: 30 * 1000, enabled: opts.enabled ?? true },
  )

  const mentionQuery = useQuery(
    [...baseKey, 'mention'],
    () =>
      occurrencesService.listProcesses({
        filter: 'mention',
        search: opts.search,
        scope: opts.scope,
        page: 1,
        limit: 1,
      }),
    { staleTime: 30 * 1000, enabled: opts.enabled ?? true },
  )

  return {
    totals: allQuery.data?.totals ?? null,
    mention: mentionQuery.data?.totals.total_processes ?? 0,
    isLoading: allQuery.isLoading || mentionQuery.isLoading,
  }
}

/**
 * Busca dados de UM processo específico pelo id (útil quando o usuário entra
 * via deep-link do e-mail mas o processo não está na primeira página).
 */
export function useProcessSidebarInfo(
  processId: number | null,
  /** Item já carregado na lista — evita a tela de "carregando" ao abrir;
   * ainda refaz a busca em segundo plano (`initialDataUpdatedAt: 0`). */
  initialData?: ProcessListItem,
) {
  return useQuery(
    processId
      ? ['occurrences', 'process-info', processId]
      : ['occurrences', 'process-info', 'idle'],
    () => occurrencesService.getProcessInfo(processId as number),
    {
      enabled: !!processId,
      staleTime: 60 * 1000,
      // 403/404 (fora do escopo, ou sem o módulo) não muda com nova tentativa.
      retry: false,
      initialData,
      initialDataUpdatedAt: initialData ? 0 : undefined,
    },
  )
}
